/**
 * Archival Containers — the estate view.
 *
 * The first end-user surface in the platform: a `User` sees only the archives
 * they hold a grant on, and the difference from an administrator's view is
 * visible without anyone explaining it.
 */

import {
  Alert,
  Badge,
  Button,
  Card,
  Grid,
  Group,
  Skeleton,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconArchive,
  IconDatabase,
  IconInbox,
  IconLock,
  IconPlus,
  IconSearch,
  IconSnowflake,
} from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { useAccessToken } from "@/features/auth/useAccessToken";
import { useMe } from "@/features/users/useMe";
import { listArchiveContainers, type ArchiveContainer } from "@/shared/api/archive";
import {
  CLUSTER_LABEL,
  clusterColour,
  clusterTitle,
  formatBytes,
  formatCount,
  formatDate,
} from "./format";

export function ArchiveContainersPage() {
  const getToken = useAccessToken();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const isAdmin = (me?.roles ?? []).includes("administrator");
  const [containers, setContainers] = useState<ArchiveContainer[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const page = await listArchiveContainers(token);
      setContainers(page.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalRecords = containers?.reduce((sum, c) => sum + c.record_count, 0) ?? 0;
  const totalBytes = containers?.reduce((sum, c) => sum + c.bytes_total, 0) ?? 0;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={3}>Archival Containers</Title>
          <Text size="sm" c="dimmed" mt={4}>
            {containers?.length
              ? `${containers.length} containers · ${formatCount(totalRecords)} records · ${formatBytes(totalBytes)} under retention`
              : "Governed archives across Clusters A, B and C"}
          </Text>
        </div>
        <Group gap="xs">
          <Button
            component={RouterLink}
            to="/archive"
            variant="light"
            leftSection={<IconSearch size={16} />}
          >
            Search &amp; retrieve
          </Button>
          {isAdmin && (
            <Button
              component={RouterLink}
              to="/archive/containers/new"
              leftSection={<IconPlus size={16} />}
            >
              New archive
            </Button>
          )}
        </Group>
      </Group>

      {error && (
        <Alert color="red" variant="light" title="Could not load archives">
          {error}
        </Alert>
      )}

      {!containers && !error && (
        <Grid>
          {[0, 1, 2, 3].map((i) => (
            <Grid.Col key={i} span={{ base: 12, md: 6 }}>
              <Skeleton height={168} radius="md" />
            </Grid.Col>
          ))}
        </Grid>
      )}

      {containers?.length === 0 && (
        <Card withBorder padding="xl" radius="md">
          <Stack align="center" gap="xs">
            <IconInbox size={32} color="var(--mantine-color-dimmed)" />
            <Text fw={500}>No archives visible</Text>
            <Text size="sm" c="dimmed" ta="center" maw={460}>
              Access is granted per archival container by an administrator. Ask for a grant to see
              archived content.
            </Text>
          </Stack>
        </Card>
      )}

      <Grid>
        {containers?.map((container) => {
          const isDataset = container.kind === "datasets";
          const target = isDataset
            ? `/archive/containers/${container.container_id}/datasets`
            : `/archive/containers/${container.container_id}`;
          return (
            <Grid.Col key={container.container_id} span={{ base: 12, md: 6 }}>
              <Card withBorder padding="lg" radius="md" h="100%">
                <Stack gap="sm" h="100%" justify="space-between">
                  <div>
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <Group gap="xs" wrap="nowrap">
                        {isDataset ? (
                          <IconDatabase size={20} color="var(--mantine-color-violet-4)" />
                        ) : (
                          <IconArchive size={20} color="var(--mantine-color-teal-4)" />
                        )}
                        <div>
                          <Text fw={600}>{container.name}</Text>
                          <Text size="xs" c="dimmed">
                            {container.source_system}
                            {container.decommissioned_on
                              ? ` · decommissioned ${formatDate(container.decommissioned_on)}`
                              : ""}
                          </Text>
                        </div>
                      </Group>
                      <Tooltip label={CLUSTER_LABEL[container.cluster]} multiline w={220}>
                        <Badge color={clusterColour(container.cluster)} variant="light">
                          {clusterTitle(container.cluster)}
                        </Badge>
                      </Tooltip>
                    </Group>
                    <Text size="sm" c="dimmed" mt="sm" lineClamp={2}>
                      {container.description}
                    </Text>
                  </div>

                  <div>
                    <Group gap="xl" mt="md">
                      <Stat
                        label={isDataset ? "Rows preserved" : "Records"}
                        value={formatCount(container.record_count)}
                      />
                      <Stat label="Size" value={formatBytes(container.bytes_total)} />
                      <Stat label="Retention" value={container.retention_class} />
                    </Group>

                    <Group justify="space-between" mt="md">
                      <Group gap={6}>
                        {container.storage_class === "GLACIER_IR" && (
                          <Tooltip label="Glacier Instant Retrieval — cold storage, no retrieval delay">
                            <Badge
                              size="sm"
                              variant="default"
                              tt="none"
                              leftSection={<IconSnowflake size={11} />}
                            >
                              Glacier IR
                            </Badge>
                          </Tooltip>
                        )}
                        {container.object_lock_mode && (
                          <Tooltip
                            label={
                              container.storage_enforces_retention
                                ? `Object Lock ${container.object_lock_mode} — enforced by the storage layer`
                                : `Retention recorded but not enforced on the ${container.storage_backend} backend`
                            }
                          >
                            <Badge
                              size="sm"
                              variant="default"
                              tt="none"
                              leftSection={<IconLock size={11} />}
                            >
                              {container.storage_enforces_retention
                                ? "Object Lock"
                                : "Retention recorded"}
                            </Badge>
                          </Tooltip>
                        )}
                        {container.default_legal_hold && (
                          <Badge size="sm" color="orange" variant="light" tt="none">
                            Legal hold
                          </Badge>
                        )}
                        <Badge size="sm" variant="default" tt="none">
                          {container.lifecycle_state}
                        </Badge>
                      </Group>
                      <Button variant="light" size="xs" onClick={() => navigate(target)}>
                        {isDataset ? "Browse tables" : "Open"}
                      </Button>
                    </Group>
                  </div>
                </Stack>
              </Card>
            </Grid.Col>
          );
        })}
      </Grid>
    </Stack>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: 0.4 }}>
        {label}
      </Text>
      <Text size="lg" fw={600}>
        {value}
      </Text>
    </div>
  );
}
